import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import { TagIcon } from "@heroicons/react/20/solid";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { clsx } from "clsx";
import { Badge } from "src/components/base/badge";
import { Link } from "src/components/base/link";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Fieldset } from "@headlessui/react";
import { Field, FieldGroup, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import TablePagination from "src/components/table-pagination";
import { Subheading } from "src/components/base/heading";

/**
 * Type that defines the search parameter of this endpoint
 */
type RouteSearch = {
    /** The current page number we're on */
    page: number;
    /** The search term that should be used */
    search: string;
    /** The type of filter to use */
    filter: RouteFilter;
};

/** Filter applied to this route */
type RouteFilter = "all" | "installed" | "not_installed";

const LIMIT = 25;

export const Route = createFileRoute("/_admin/a/plugins/")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): RouteSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
            filter: (search.filter as RouteFilter) || "all",
        };
    },
    loaderDeps: ({ search: { page, search, filter } }) => ({ page, search, filter }),
    loader: async ({ deps }) => {
        return await ApiInternal.plugins.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
            filter_installed: (
                {
                    all: undefined,
                    installed: true,
                    not_installed: false,
                } as Record<RouteFilter, boolean | undefined>
            )[deps.filter],
        });
    },
});

/** Overview of all plugins */
function RouteComponent() {
    const [t] = useTranslation("admin-plugin-overview");

    const navigate = useNavigate();
    const { page, filter, search } = Route.useSearch();
    const { items: plugins, ...data } = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search,
            filter,
        },
    });

    return (
        <HeadingLayout heading={t("heading.plugin-overview")} headingDescription={t("description.plugin-overview")}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/plugins",
                                        search: {
                                            page: 1,
                                            filter,
                                            search: fieldApi.state.value,
                                        },
                                    });
                                },
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.search")}</Label>
                                    <Input
                                        placeholder={t("label.name")}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"filter"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/plugins",
                                        search: {
                                            page: 1,
                                            search,
                                            filter: fieldApi.state.value,
                                        },
                                    });
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Listbox
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e as RouteFilter)}
                                    >
                                        <ListboxOption value={"all"}>
                                            <ListboxLabel>{t("label.show-all")}</ListboxLabel>
                                        </ListboxOption>
                                        <ListboxOption value={"installed"}>
                                            <ListboxLabel>{t("label.show-installed")}</ListboxLabel>
                                        </ListboxOption>
                                        <ListboxOption value={"not-installed"}>
                                            <ListboxLabel>{t("label.show-not-installed")}</ListboxLabel>
                                        </ListboxOption>
                                    </Listbox>
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>

            <div className={"grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5"}>
                {plugins.map((plugin) => (
                    <Link
                        key={plugin.uuid}
                        className={clsx(
                            "block max-w-sm p-2",
                            "rounded-lg border shadow-xs",
                            "bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50",
                            "data-hover:bg-zinc-950/5 dark:data-hover:bg-white/10",
                        )}
                        href={"/a/plugins/$pluginId"}
                        params={{ pluginId: plugin.uuid }}
                    >
                        <img
                            className={"max-h-40 w-full rounded-lg object-cover"}
                            alt={t("label.plugin-image")}
                            src={`data:image/png;base64,${plugin.image}`}
                        />
                        <div className={"flex flex-col items-center justify-center gap-2 pt-6 pb-4"}>
                            <Subheading>{plugin.name}</Subheading>
                            <div className={"flex flex-wrap items-center justify-center gap-2"}>
                                {plugin.installed && <Badge color={"cyan"}>{t("label.installed")}</Badge>}
                                {plugin.outdated && (
                                    <Badge color={"orange"} title={t("description.outdated")}>
                                        {t("label.outdated")}
                                    </Badge>
                                )}
                                {!plugin.store && (
                                    <Badge color={"zinc"} title={t("description.local")}>
                                        {t("label.local")}
                                    </Badge>
                                )}
                                {plugin.installed && (
                                    <Badge>
                                        <TagIcon className={"size-4"} />
                                        {plugin.version}
                                    </Badge>
                                )}
                                {plugin.store &&
                                    (!plugin.installed || plugin.store.current_version !== plugin.version) && (
                                        <Badge>
                                            <TagIcon className={"size-4"} />
                                            {plugin.store.current_version}
                                        </Badge>
                                    )}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            <Suspense>
                <TablePagination
                    currentPage={page}
                    maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                    href={"/a/rulesets"}
                    getSearchParams={(newPage: number) => ({ search, page: newPage })}
                />
            </Suspense>
        </HeadingLayout>
    );
}
